import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Collapse,
} from '@mui/material';
import {
  Code,
  CheckCircle,
  TextFields,
  RemoveCircleOutline,
  HelpOutline,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { ProcessVariable } from '../types/process';
import { JsonViewer } from './JsonViewer';

interface CompactVariablesTableProps {
  /** Lista de todas as variáveis */
  variables: ProcessVariable[];
  /** Função para alternar visualização detalhada */
  onToggleDetailed?: (variableId: string) => void;
  /** Variáveis que estão em visualização detalhada */
  detailedVariables?: Set<string>;
}

/**
 * Componente para exibir todas as variáveis em formato de tabela compacta com expansão inline
 */
export const CompactVariablesTable: React.FC<CompactVariablesTableProps> = ({
  variables,
  onToggleDetailed,
  detailedVariables = new Set(),
}) => {
  // Função para verificar se deve usar JsonViewer
  const shouldUseJsonViewer = (variable: ProcessVariable): boolean => {
    return variable.type === 'Json' || 
           (typeof variable.value === 'object' && variable.value !== null);
  };
  // Função para obter ícone do tipo de variável
  const getVariableTypeIcon = (type: string) => {
    switch (type) {
      case 'Json':
        return <Code />;
      case 'Boolean':
        return <CheckCircle />;
      case 'String':
        return <TextFields />;
      case 'Null':
        return <RemoveCircleOutline />;
      default:
        return <HelpOutline />;
    }
  };

  // Função para obter cor do tipo
  const getTypeColor = (type: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (type) {
      case 'Json':
        return 'primary';
      case 'Boolean':
        return 'success';
      case 'String':
        return 'info';
      case 'Integer':
      case 'Long':
      case 'Double':
        return 'warning';
      case 'Null':
        return 'default';
      default:
        return 'secondary';
    }
  };

  // Função para formatar valor de forma compacta
  const formatCompactValue = (variable: ProcessVariable): string => {
    if (variable.value === null || variable.value === undefined) {
      return 'null';
    }

    if (shouldUseJsonViewer(variable)) {
      return typeof variable.value === 'string' ? 'JSON' : 'Object';
    }

    const value = String(variable.value);
    // Truncar valores muito longos
    return value.length > 50 ? `${value.substring(0, 50)}...` : value;
  };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Nome</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Tipo</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Estado</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Valor</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Criado em</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 60, textAlign: 'center' }}>Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {variables.map((variable) => {
            const isDetailed = detailedVariables.has(variable.id);
            const isExpandable = shouldUseJsonViewer(variable) || String(variable.value).length > 50;

            return (
              <React.Fragment key={variable.id}>
                <TableRow hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getVariableTypeIcon(variable.type)}
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 500,
                          fontSize: '0.875rem',
                        }}
                      >
                        {variable.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={variable.type}
                      size="small"
                      color={getTypeColor(variable.type)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={variable.state}
                      size="small"
                      variant="outlined"
                      color="secondary"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        color: variable.value === null ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {formatCompactValue(variable)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {new Date(variable.create_time).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {isExpandable && onToggleDetailed && (
                      <Tooltip title={isDetailed ? 'Recolher detalhes' : 'Expandir detalhes'}>
                        <IconButton
                          size="small"
                          onClick={() => onToggleDetailed(variable.id)}
                          color={isDetailed ? 'primary' : 'default'}
                        >
                          {isDetailed ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
                
                {/* Linha expandida com detalhes */}
                {isDetailed && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none' }}>
                      <Collapse in={isDetailed} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            <strong>Valor detalhado:</strong>
                          </Typography>
                          {shouldUseJsonViewer(variable) ? (
                            <JsonViewer
                              data={variable.value}
                              maxHeight="400px"
                            />
                          ) : (
                            <Box
                              sx={{
                                backgroundColor: 'background.paper',
                                p: 2,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.200',
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {String(variable.value)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CompactVariablesTable;
